import { useCallback, useEffect, useState } from 'react'

import config from '@config'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useUser from '@hooks/useUser'

import { doParseStringifiedData, setExperienceCookie } from './utils'
import useAuthentication from '@hooks/useAuthentication'

const {
  auth: { ssoSourceSuffix },
} = config

export interface PianoEvents {
  onShowOffer: (params: any) => void
  onCheckoutCustom: (event: any) => void
  onCheckoutClose: (event: any) => void
}

interface PianoTrackingEventParameters {
  eventCategory?: string
  buttonText?: string
  offerId?: string
  templateId?: string
  templateVariantId?: string
  campaign?: string
  url?: string
  loginCase?: string
  articleId?: string
  experienceId?: string
  overlay_button?: string
}

interface PianoTrackingEvent {
  type?: string
  params?: PianoTrackingEventParameters
}

const usePianoEvents = (): PianoEvents => {
  const [trackingEvent, setTrackingEvent] = useState<PianoTrackingEvent>({})

  const userData = useUser()
  const { login } = useAuthentication({ prefix: 'PIA' })

  const trackPianoEvent = useCallback<TrackingFnType<PianoTrackingEvent>>(
    ({ extraData }) => {
      const { type, params } = extraData

      return {
        event: type,
        eventCategory: params?.eventCategory,
        eventAction: params?.buttonText,
        overlay_offerID: params?.offerId,
        overlay_templateID: params?.templateId,
        overlay_templateVariantID: params?.templateVariantId,
        // external events in Piano template
        overlay_campaign: params?.campaign,
        overlay_link: params?.url,
        overlay_loginCase: params?.loginCase,
        overlay_articleID: params?.articleId,
        overlay_experienceID: params?.experienceId,
        overlay_button: params?.overlay_button,
      }
    },
    []
  )

  const trackingFunction = useTracking<PianoTrackingEvent>(trackPianoEvent)

  useEffect(() => {
    if (trackingEvent?.type) {
      trackingFunction(trackingEvent)
    }
  }, [trackingEvent, trackingFunction])

  const onCheckoutClose = (event: any) => {
    // Default behavior is to refresh the page on successful checkout
    if (event?.state == 'checkoutCompleted') {
      location?.reload?.()
    }
  }

  const onCheckoutCustom = (event: any) => {
    const { eventName } = event
    const { params, ...rest } = event?.params
    const parsedParams = doParseStringifiedData(params)

    const mergedParams = {
      ...(parsedParams ?? {}), // data about Piano experience metadata
      ...rest, // external events in Piano template
    }

    switch (eventName) {
      case 'overlay_to_external_link':
      case 'overlay_to_article':
        setTrackingEvent({
          type: eventName,
          params: mergedParams,
        })

        break

      case 'adblock_interaction':
        setTrackingEvent({
          type: eventName,
          params: { ...mergedParams, eventCategory: 'adblock' },
        })

        break

      case 'overlay_to_login':
        if (!userData) {
          setExperienceCookie(parsedParams)

          const { offerId, templateId, templateVariantId, experienceId } =
            parsedParams

          // Gets the source from pianos custom variable, set on every page change
          // and appends the piano specific event data
          const source = `${
            window?.tp?.customVariables?.sourceSSO
          }_${offerId}_${templateId}${
            templateVariantId ? `_${templateVariantId}` : ''
          }_${experienceId}_${ssoSourceSuffix}`

          login({
            source,
            loginCase: rest?.loginCase || 'email_only',
          })
        }

        setTrackingEvent({
          type: eventName,
          params: { ...mergedParams, overlay_button: 'toLogin' },
        })

        break

      case 'overlay_close':
        setTrackingEvent({
          type: eventName,
          params: { ...mergedParams, overlay_button: 'close' },
        })

        break
    }
  }

  const onShowOffer = (params: any) => {
    setTrackingEvent({ type: 'overlay_show', params })
  }

  return {
    onCheckoutClose,
    onCheckoutCustom,
    onShowOffer,
  }
}

export default usePianoEvents
